import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, alpha } from '@mui/material';
import Iconify from '../../../components/iconify';
import { useSystemHealth } from '../../../hooks/useSystemHealth';

// ----------------------------------------------------------------------

type RowProps = {
  icon: string;
  label: string;
  up: boolean;
  upLabel: string;
  downLabel: string;
};

function StatusRow({ icon, label, up, upLabel, downLabel }: RowProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Iconify icon={icon} width={18} sx={{ color: 'text.secondary' }} />
      <Typography variant="caption" sx={{ flexGrow: 1, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: up ? 'success.main' : 'error.main',
        }}
      />
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: up ? 'success.main' : 'error.main' }}
      >
        {up ? upLabel : downLabel}
      </Typography>
    </Stack>
  );
}

export default function NavSystemStatus() {
  const { t } = useTranslation();
  const { aws, voice, reachable } = useSystemHealth(60);

  const awsUp = reachable && aws === 'UP';
  const voiceUp = reachable && voice === 'UP';

  return (
    <Box sx={{ px: 2.5, mt: 1.5 }}>
      <Stack
        spacing={1.25}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
          border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.12)}`,
        }}
      >
        <Typography variant="overline" sx={{ color: 'text.disabled', lineHeight: 1 }}>
          {String(t('nav.health.system_status'))}
        </Typography>

        <StatusRow
          icon="mdi:server-network"
          label={String(t('nav.health.aws_server'))}
          up={awsUp}
          upLabel={String(t('nav.health.online'))}
          downLabel={String(t('nav.health.offline'))}
        />

        <StatusRow
          icon="mdi:microphone"
          label={String(t('nav.health.voice_app'))}
          up={voiceUp}
          upLabel={String(t('nav.health.online'))}
          downLabel={String(t('nav.health.offline'))}
        />
      </Stack>
    </Box>
  );
}